import { Inject, Injectable, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { Router } from '@angular/router';

import { BehaviorSubject, Subscription, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { PortfolioCategory } from '../../enums/portfolio-category.enum';
import { PortfolioRegion } from '../../enums/portfolio-region.enum';
import { TargetingListsApiService } from '../../../../api/targeting/targeting-lists.api.service';
import { DiscoverRouteTypeEnum } from '../../../DiscoverRouter/enums/discover-route-type.enum';
import { DiscoverRoutingService } from '../../../DiscoverRouter/services/discover-routing.service';
import { PortfolioModel } from './portfolio.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

@Injectable()
export class PortfolioService implements OnDestroy {
    region: PortfolioRegion = PortfolioRegion.UNITED_STATES;
    entityType: EntityType = EntityType.CONTACT;
    category: PortfolioCategory = PortfolioCategory.NATIONAL_NEWS_MEDIA;
    subCategory: PortfolioCategory = PortfolioCategory.ENERGY_AND_ENVIRONMENT;

    portfolios: PortfolioModel[];
    private portfolios$: BehaviorSubject<PortfolioModel[]> = new BehaviorSubject<PortfolioModel[]>(null);
    selectedPortfolioId: number;

    private subscriptions: Subscription[] = [];
    private locale: string = 'en-US';

    readonly regionOptions = [
        {
            value: PortfolioRegion.UNITED_STATES,
            label: `Discover.portfolios.regions.${PortfolioRegion.UNITED_STATES}`,
            flag : this.appBaseHref + 'assets/images/us.png'
        },
        {
            value: PortfolioRegion.UNITED_KINGDOM,
            label: `Discover.portfolios.regions.${PortfolioRegion.UNITED_KINGDOM}`,
            flag : this.appBaseHref + 'assets/images/uk.png'
        },
        {
            value: PortfolioRegion.CANADA,
            label: `Discover.portfolios.regions.${PortfolioRegion.CANADA}`,
            flag : this.appBaseHref + 'assets/images/ca.png'
        },
        {
            value: PortfolioRegion.FRANCE,
            label: `Discover.portfolios.regions.${PortfolioRegion.FRANCE}`,
            flag : this.appBaseHref + 'assets/images/fr.png'
        }
    ];

    readonly categoryOptions = [
        {
            value: PortfolioCategory.NATIONAL_NEWS_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.NATIONAL_NEWS_MEDIA}`,
        },
        {
            value: PortfolioCategory.REGIONAL_NEWS_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.REGIONAL_NEWS_MEDIA}`,
        },
        {
            value: PortfolioCategory.CULTURAL_NEWS_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.CULTURAL_NEWS_MEDIA}`,
        },
        {
            value: PortfolioCategory.LEADING_BY_SECTOR,
            label: `Discover.portfolios.categories.${PortfolioCategory.LEADING_BY_SECTOR}`,
        }
    ];

    readonly culturalCategories = [
        {
            value: PortfolioCategory.BLACK_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.BLACK_MEDIA}`,
        },
        {
            value: PortfolioCategory.HISPANIC_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.HISPANIC_MEDIA}`,
        },
        {
            value: PortfolioCategory.INDIGENOUS_NORTH_AMERICAN_MEDIA,
            label: `Discover.portfolios.categories.${PortfolioCategory.INDIGENOUS_NORTH_AMERICAN_MEDIA}`,
        }
    ];

    readonly sectorCategories = [
        {
            value: PortfolioCategory.ENERGY_AND_ENVIRONMENT,
            label: `Discover.portfolios.categories.${PortfolioCategory.ENERGY_AND_ENVIRONMENT}`,
            icon : 'fa-regular fa-duotone fa-eye'
        },
        {
            value: PortfolioCategory.FINANCE,
            label: `Discover.portfolios.categories.${PortfolioCategory.FINANCE}`,
            icon : 'fa-regular fa-duotone fa-eye'
        },
        {
            value: PortfolioCategory.POLITICS,
            label: `Discover.portfolios.categories.${PortfolioCategory.POLITICS}`,
            icon : 'fa-regular fa-duotone fa-eye'
        },
        {
            value: PortfolioCategory.HEALTHCARE,
            label: `Discover.portfolios.categories.${PortfolioCategory.HEALTHCARE}`,
            icon : 'fa-regular fa-duotone fa-eye'
        },
        {
            value: PortfolioCategory.TECHNOLOGY,
            label: `Discover.portfolios.categories.${PortfolioCategory.TECHNOLOGY}`,
            icon : 'fa-regular fa-duotone fa-eye'
        },
        {
            value: PortfolioCategory.TRAVEL_AND_TOURISM,
            label: `Discover.portfolios.categories.${PortfolioCategory.TRAVEL_AND_TOURISM}`,
            icon : 'fa-regular fa-duotone fa-eye'
        }
    ];

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public discoverRoutingService: DiscoverRoutingService,
        public targetingListsApiService: TargetingListsApiService,
        public router: Router,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (route.type === DiscoverRouteTypeEnum.portfolioResults) {
                        if (route.value) {
                            this.selectedPortfolioId = +route.value;
                            this.setSelectedParameters();
                        }
                    }
                }
            ),
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x),
                take(1)
            ).subscribe(languageTag => {
                this.locale = languageTag;
            }),
            this.targetingListsApiService.getPortfolios().subscribe(apiModels => {
                this.portfolios = apiModels.map(apiModel => new PortfolioModel(apiModel, this.locale));
                this.setSelectedParameters();
                this.portfolios$.next(this.portfolios);
            })
        );
    }

    getPortfolios(): Observable<PortfolioModel[]> {
        return this.portfolios$;
    }

    goToResults() {
        const foundPortfolio = this.portfolios.find(portfolio => {
            if (this.category === PortfolioCategory.CULTURAL_NEWS_MEDIA ||
                this.category === PortfolioCategory.LEADING_BY_SECTOR) {
                return portfolio.metadata.category === this.subCategory &&
                    portfolio.metadata.region === this.region &&
                    portfolio.metadata.entityType === this.entityType;
            } else {
                return portfolio.metadata.category === this.category &&
                    portfolio.metadata.region === this.region &&
                    portfolio.metadata.entityType === this.entityType;
            }
        });

        if (foundPortfolio) {
            this.router.navigate(['Discover', 'portfolios', 'results', foundPortfolio.targetingListId]);
        } else {
            // Should not happen on prod
            this.subCategory = PortfolioCategory.ENERGY_AND_ENVIRONMENT;
            alert('Industry list does not exist');
            this.router.navigate(['Discover', 'portfolios']);
        }

    }

    setSelectedParameters() {
        if (this.selectedPortfolioId && this.portfolios) {
            const portfolio = this.portfolios.find(x => x.targetingListId === this.selectedPortfolioId);
            this.region = portfolio.metadata.region;
            this.category = portfolio.metadata.category;
            this.entityType = portfolio.metadata.entityType;
            this.subCategory = portfolio.metadata.category;

            if (this.culturalCategories.find(x => x.value === portfolio.metadata.category)) {
                this.category = PortfolioCategory.CULTURAL_NEWS_MEDIA;
            }
            if (this.sectorCategories.find(x => x.value === portfolio.metadata.category)) {
                this.category = PortfolioCategory.LEADING_BY_SECTOR;
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
